/**
 * Two languages, one dictionary.
 *
 * Every player-facing string lives here rather than in the markup, so a missing
 * translation is a type error at build time instead of a Turkish sentence left
 * behind on an English screen. The operator panel is not in here: it has one
 * reader, who speaks Turkish.
 *
 * Turkish is the default because that is who plays this today; a browser that
 * asks for anything else gets English.
 */

export const LANGUAGES = ["tr", "en"] as const;
export type Language = (typeof LANGUAGES)[number];

export const LANGUAGE_STORAGE_KEY = "orbit.lang";

const tr = {
  // --- start screen ---------------------------------------------------------
  tagline: "Yörüngeni büyüt. Enerjini fırlat. Rakibin küresini çalıp daha güçlü dön.",
  mechanicCollect: "TOPLA",
  mechanicCollectHint: "Parçacıkları yakala",
  mechanicLaunch: "FIRLAT",
  mechanicLaunchHint: "İsabette geri döner",
  mechanicSteal: "ÇAL",
  mechanicStealHint: "Rakip yörüngesini sök",
  mechanicShield: "KORU",
  mechanicShieldHint: "Atışları küreye çevir",
  nickLabel: "TAKMA AD",
  nickAria: "Takma ad",
  play: "OYNA",
  signInGoogle: "Google ile giriş yap",
  signedInAs: "Arenaya {name} olarak giriyorsun",
  signOut: "çıkış",
  seeStandings: "SIRALAMAYI GÖR",
  privacy: "GİZLİLİK",
  controlsHint: "WASD ile hareket · Fareyle nişan al · SOL TIK ile fırlat",
  nickRequired: "Oynamak için bir takma ad yaz.",
  signInFailed: "Google girişi tamamlanamadı. Tekrar deneyin.",
  startAria: "Oyuna başla",

  // --- heads-up display -----------------------------------------------------
  score: "SKOR",
  orbCounter: "YÖRÜNGE KÜRESİ",
  surgeReady: "KÜRE ÇALAR",
  liveRanking: "CANLI SIRALAMA",
  rankingAria: "Skor sıralaması",
  radar: "ARENA RADARI",
  radarAria: "Arena mini haritası",
  players: "{count} OYUNCU",
  range: "MENZİL",
  leave: "ÇIK",
  leaveAria: "Arenadan ayrıl",
  pauseAria: "Oyunu duraklat",
  soundOn: "Sesi aç",
  soundOff: "Sesi kapat",
  settingsAria: "Arena ayarları",
  helpAria: "Nasıl oynanır",
  timeLeftAria: "Kalan süre {time}",
  endlessAria: "Süresiz arena",
  keyMove: "HAREKET",
  keyAim: "NİŞAN + ATEŞ",
  mouseLeft: "SOL TIK",
  keyLaunch: "FIRLAT",
  keyShield: "KALKAN",
  keyBoost: "BOOST",

  // --- connection -----------------------------------------------------------
  connecting: "ARENAYA BAĞLANILIYOR…",
  reconnecting: "BAĞLANTI KOPTU · YENİDEN DENENİYOR…",
  disconnected: "ARENADAN ÇIKILDI",
  preparing: "HAZIRLANIYOR…",
  joinedRoom: "{room} · ARENAYA GİRDİN",
  roomLabel: "ODA {number}",
  chooseRoom: "ODA SEÇ",
  roomsTitle: "Odalar",
  roomsAria: "Oda seçimi",
  roomsHint: "Seçmezsen sunucu seni en kalabalık boş odaya koyar.",
  roomsAuto: "SUNUCU SEÇSİN",
  roomsPlayers: "{count}/{capacity} oyuncu",
  roomsBots: "{count} bot",
  roomsEmpty: "Şu an açık oda yok. Oynamaya başlayınca ilk oda açılır.",
  roomsFull: "DOLU",

  // --- events ---------------------------------------------------------------
  toastReady: "HAZIRSIN · ENERJİLERİ TOPLA!",
  toastLaunched: "KÜRE FIRLATILDI",
  toastShield: "KALKAN AÇILDI · ATIŞLARI YAKALA!",
  toastShieldCatch: "RAKİP ATIŞI YAKALANDI! +1 KÜRE",
  toastBoost: "NOVA BOOST · 1 KÜRE YAKILDI",
  toastStole: "{name} ENERJİSİ ÇALINDI!",
  toastRobbed: "{name} KÜRENİ ÇALDI!",
  // Streak names. Short enough to read in the half second they are on screen, and
  // escalating in tone rather than in length.
  streak2: "ÇİFTE AV",
  streak3: "ÜÇLEME",
  streak5: "AMANSIZ",
  streak7: "DURDURULAMAZ",
  streak10: "HÜKÜMDAR",
  streakCount: "{count} AVLIK SERİ",
  killedAria: "yok etti",
  // The hunted. "AVLANAN" is the one being hunted, not the hunter — the passive is
  // deliberate and it is the word the whole mechanic is named after.
  toastHuntedYou: "AVLANAN SENSİN! HERKES SENİN PEŞİNDE",
  huntedYou: "AVLANAN SENSİN · ÇİFT PUAN",
  huntedAria: "Avlanan",
  toastEliminated: "ÇEKİRDEĞİN PARÇALANDI",
  toastKill: "{name} NOVA OLDU! +{score}",
  // The storm. The warning line names the seconds because that is the number the
  // player is deciding on: whether there is time to get there.
  toastStormWarning: "ENERJİ FIRTINASI {seconds} SANİYE SONRA! RADARA BAK",
  toastStormOpen: "FIRTINA AÇILDI! İÇİNDE DURDUKÇA DOLARSIN · {count} KÜREYE KADAR",
  stormWarnMark: "FIRTINA {seconds}s",
  stormOpenMark: "FIRTINA · {seconds}s",
  toastTimeUp: "SÜRE DOLDU",
  toastMatchOver: "MAÇ BİTTİ · YENİ TUR BAŞLIYOR",
  opponent: "RAKİP",
  you: "SEN",
  toastCharged: "{ring}. YÖRÜNGE TAM · SONRAKİ ATIŞ {orbs} KÜRE ÇALAR",

  // --- tutorial -------------------------------------------------------------
  tutorialSkip: "GEÇ",
  tutorialSkipAria: "Eğitimi geç",
  tutorialStepAria: "Eğitim adımı {step} / {total}",
  tutorialDone: "EĞİTİM TAMAMLANDI · ARENA SENİN!",
  tutorialCollect: "KENDİ RENGİNİ TOPLA",
  tutorialCollectCopy: "Kendi rengin küreye, rakip renkleri puana dönüşür.",
  tutorialLaunch: "KÜRE FIRLAT",
  tutorialLaunchCopy: "Fareyle nişan al, sol tıkla fırlat. Iskaladığın küreyi kaybedersin.",
  tutorialSteal: "ENERJİ ÇAL",
  tutorialStealCopy: "Atışını rakip yörüngesine isabet ettir. Çalınan renk sana geri döner.",
  tutorialShield: "ATIŞI YAKALA",
  tutorialShieldCopy: "Rakip atışı yaklaşırken E'ye veya kalkan düğmesine bas.",

  // --- pause and result -----------------------------------------------------
  pausedEyebrow: "OYUN DURAKLATILDI",
  pausedAria: "Oyun duraklatıldı",
  pausedTitle: "Yörünge seni bekliyor.",
  resume: "DEVAM ET",
  resultAria: "Arena sonucu",
  resultEyebrow: "ARENA SONUCU",
  resultChampion: "NOVA ŞAMPİYONU!",
  resultLost: "YÖRÜNGE DAĞILDI",
  resultLastOrbs: "SON KÜRE",
  resultLeave: "ARENADAN ÇIK",
  resultContinue: "DEVAM ET",
  resultAgain: "TEKRAR OYNA",
  placedToday: "Bugün",
  placedWeek: "Bu hafta",
  placedAllTime: "Tüm zamanlar",
  placedSignInHint: "Kalıcı sıralama için Google ile giriş yap",
  placedAlone: "Bu tur sıralamaya girmedi — arenada rakip yoktu.",

  // --- help -----------------------------------------------------------------
  helpTitle: "Nasıl oynanır",
  helpCollect: "Kendi rengini topla",
  helpCollectCopy: "Kendi rengin küreye, rakip renkleri puana dönüşür.",
  helpLaunch: "Küreyi fırlat",
  helpLaunchCopy: "Iskalarsan küreni kaybedersin; isabet ederse geri döner.",
  helpSteal: "Rakipten çal",
  helpStealCopy: "İsabet eden küren rakibin enerjisiyle birlikte sana geri döner.",
  helpShield: "Tam zamanında korun",
  helpShieldCopy: "Kalkan, rakip atışını kendi yörünge kürene dönüştürür.",
  helpBoost: "Küre yakarak kaç",
  helpBoostCopy: "SHIFT veya BOOST, bir küre karşılığında bir hız darbesi verir.",
  helpRisk: "Her saldırı aynı zamanda bir risk.",
  helpClose: "ANLADIM",
  helpEyebrow: "OYUNUN FARKI",
  helpReplayTutorial: "EĞİTİMİ TEKRARLA",
  close: "Kapat",

  // --- settings -------------------------------------------------------------
  settingsTitle: "Arena Ayarları",
  settingsAriaLabel: "Arena ayarları",
  settingsClose: "Ayarları kapat",
  settingsColor: "Çekirdek rengi",
  settingsTheme: "Arena teması",
  settingsNote:
    "Denge ayarları — atış menzili, hız, küre sayısı, çekirdek ölçüleri — arenanın tamamını ilgilendirdiği için sunucudan gelir ve burada değiştirilemez. Online oynarken çekirdek rengini de arena atar.",
  settingsApply: "AYARLARI UYGULA",
  settingsReset: "VARSAYILANA DÖN",
  settingsHint: "Görsel ayarlar anında, maç süresi yeni maçta uygulanır.",
  settingsEyebrow: "PROTOTİP YÖNETİMİ",
  settingsAppearance: "GÖRÜNÜM",
  aimRange: "MENZİL",
  shieldAria: "Enerji kalkanı",
  launchAria: "Yörünge küresi fırlat",
  boostAria: "Nova boost",
  firstToast: "ENERJİ TOPLA · YÖRÜNGENİ BÜYÜT",
  languageAria: "Dil",

  // --- standings page -------------------------------------------------------
  standingsTitle: "SIRALAMA",
  standingsSub: "N Nova: Orbit Clash · dünya listesi",
  standingsBack: "ARENAYA DÖN",
  periodDaily: "BUGÜN",
  periodDailyBlurb: "Her gün gece yarısı sıfırlanır. Misafirler de burada.",
  periodWeekly: "BU HAFTA",
  periodWeeklyBlurb: "Pazartesi başlar. Girişli oyunculara ait.",
  periodAllTime: "TÜM ZAMANLAR",
  periodAllTimeBlurb: "Kalıcı rekorlar. Girişli oyunculara ait.",
  standingsLoading: "Yükleniyor…",
  standingsFailed: "Sıralama şu an alınamadı. Birazdan tekrar denenecek.",
  standingsEmptyDaily: "Bugün henüz kimse skor yapmadı. İlk sen ol.",
  standingsEmptyRanked: "Burası girişli oyunculara ait. Google ile giriş yap ve rekorunu kalıcı hale getir.",
  standingsNote:
    "Skorlar arenanın kendisi tarafından yazılır: bir can doğduğu andan bittiği ana kadar topladığı puanla listeye girer. Gönderilebilecek bir skor yoktur.",
  verifiedAccount: "Google hesabı",
  standingsDocTitle: "Sıralama · N Nova: Orbit Clash",
} as const;

/** Every key of `tr`, so a forgotten line here will not compile. */
type Dictionary = { [K in keyof typeof tr]: string };

const en: Dictionary = {
  tagline: "Grow your orbit. Launch your energy. Steal theirs and come back stronger.",
  mechanicCollect: "COLLECT",
  mechanicCollectHint: "Catch the particles",
  mechanicLaunch: "LAUNCH",
  mechanicLaunchHint: "Returns on a hit",
  mechanicSteal: "STEAL",
  mechanicStealHint: "Strip their orbit",
  mechanicShield: "SHIELD",
  mechanicShieldHint: "Turn shots into orbs",
  nickLabel: "NICKNAME",
  nickAria: "Nickname",
  play: "PLAY",
  signInGoogle: "Sign in with Google",
  signedInAs: "Entering the arena as {name}",
  signOut: "sign out",
  seeStandings: "SEE THE STANDINGS",
  privacy: "PRIVACY",
  controlsHint: "WASD to move · Aim with the mouse · LEFT CLICK to launch",
  nickRequired: "Type a nickname to play.",
  signInFailed: "Google sign-in did not complete. Try again.",
  startAria: "Start the game",

  score: "SCORE",
  orbCounter: "ORBIT ORBS",
  surgeReady: "ORBS ON HIT",
  liveRanking: "LIVE RANKING",
  rankingAria: "Score ranking",
  radar: "ARENA RADAR",
  radarAria: "Arena minimap",
  players: "{count} PLAYERS",
  range: "RANGE",
  leave: "LEAVE",
  leaveAria: "Leave the arena",
  pauseAria: "Pause the game",
  soundOn: "Turn sound on",
  soundOff: "Turn sound off",
  settingsAria: "Arena settings",
  helpAria: "How to play",
  timeLeftAria: "Time left {time}",
  endlessAria: "Endless arena",
  keyMove: "MOVE",
  keyAim: "AIM + FIRE",
  mouseLeft: "LEFT CLICK",
  keyLaunch: "LAUNCH",
  keyShield: "SHIELD",
  keyBoost: "BOOST",

  connecting: "CONNECTING TO THE ARENA…",
  reconnecting: "CONNECTION LOST · RETRYING…",
  disconnected: "LEFT THE ARENA",
  preparing: "GETTING READY…",
  joinedRoom: "{room} · YOU ARE IN",
  roomLabel: "ROOM {number}",
  chooseRoom: "CHOOSE A ROOM",
  roomsTitle: "Rooms",
  roomsAria: "Room selection",
  roomsHint: "Leave it to the server and it puts you in the fullest room with space.",
  roomsAuto: "LET THE SERVER CHOOSE",
  roomsPlayers: "{count}/{capacity} players",
  roomsBots: "{count} bots",
  roomsEmpty: "No rooms are open. The first one opens when you start playing.",
  roomsFull: "FULL",

  toastReady: "YOU ARE IN · COLLECT ENERGY!",
  toastLaunched: "ORB LAUNCHED",
  toastShield: "SHIELD UP · CATCH THEIR SHOTS!",
  toastShieldCatch: "SHOT CAUGHT! +1 ORB",
  toastBoost: "NOVA BOOST · 1 ORB BURNED",
  toastStole: "STOLE {name}'S ENERGY!",
  toastRobbed: "{name} STOLE YOUR ORB!",
  streak2: "DOUBLE",
  streak3: "TRIPLE",
  streak5: "RAMPAGE",
  streak7: "UNSTOPPABLE",
  streak10: "DOMINATING",
  streakCount: "{count} KILL STREAK",
  killedAria: "eliminated",
  toastHuntedYou: "YOU ARE HUNTED! EVERYONE IS COMING",
  huntedYou: "YOU ARE HUNTED · DOUBLE POINTS",
  huntedAria: "Hunted",
  toastEliminated: "YOUR CORE SHATTERED",
  toastKill: "{name} WENT NOVA! +{score}",
  toastStormWarning: "ENERGY STORM IN {seconds} SECONDS! CHECK THE RADAR",
  toastStormOpen: "THE STORM IS OPEN! STAND IN IT TO FILL · UP TO {count} ORBS",
  stormWarnMark: "STORM {seconds}s",
  stormOpenMark: "STORM · {seconds}s",
  toastTimeUp: "TIME IS UP",
  toastMatchOver: "MATCH OVER · NEXT ROUND STARTING",
  opponent: "OPPONENT",
  you: "YOU",
  toastCharged: "RING {ring} CLOSED · YOUR NEXT SHOT TAKES {orbs}",

  tutorialSkip: "SKIP",
  tutorialSkipAria: "Skip the tutorial",
  tutorialStepAria: "Tutorial step {step} of {total}",
  tutorialDone: "TUTORIAL DONE · THE ARENA IS YOURS!",
  tutorialCollect: "COLLECT YOUR OWN COLOUR",
  tutorialCollectCopy: "Your colour becomes an orb; their colours become points.",
  tutorialLaunch: "LAUNCH AN ORB",
  tutorialLaunchCopy: "Aim with the mouse, fire with the left button. An orb you miss with is an orb you lose.",
  tutorialSteal: "STEAL ENERGY",
  tutorialStealCopy: "Hit an opponent's orbit. What you take comes back to you.",
  tutorialShield: "CATCH A SHOT",
  tutorialShieldCopy: "Press E, or the shield button, as their shot closes in.",

  pausedEyebrow: "GAME PAUSED",
  pausedAria: "Game paused",
  pausedTitle: "The orbit is waiting.",
  resume: "RESUME",
  resultAria: "Arena result",
  resultEyebrow: "ARENA RESULT",
  resultChampion: "NOVA CHAMPION!",
  resultLost: "ORBIT SCATTERED",
  resultLastOrbs: "LAST ORBS",
  resultLeave: "LEAVE THE ARENA",
  resultContinue: "PLAY ON",
  resultAgain: "PLAY AGAIN",
  placedToday: "Today",
  placedWeek: "This week",
  placedAllTime: "All time",
  placedSignInHint: "Sign in with Google to rank for good",
  placedAlone: "This run is unranked — you had the arena to yourself.",

  helpTitle: "How to play",
  helpCollect: "Collect your own colour",
  helpCollectCopy: "Your colour becomes an orb; their colours become points.",
  helpLaunch: "Launch an orb",
  helpLaunchCopy: "Miss and you lose the orb; hit and it comes back.",
  helpSteal: "Steal from an opponent",
  helpStealCopy: "An orb that lands returns to you with their energy.",
  helpShield: "Shield at the right moment",
  helpShieldCopy: "The shield turns their shot into an orb of your own.",
  helpBoost: "Burn an orb to escape",
  helpBoostCopy: "SHIFT, or BOOST, buys a burst of speed for one orb.",
  helpRisk: "Every attack is also a risk.",
  helpClose: "GOT IT",
  helpEyebrow: "WHAT MAKES IT DIFFERENT",
  helpReplayTutorial: "REPLAY THE TUTORIAL",
  close: "Close",

  settingsTitle: "Arena Settings",
  settingsAriaLabel: "Arena settings",
  settingsClose: "Close settings",
  settingsColor: "Core colour",
  settingsTheme: "Arena theme",
  settingsNote:
    "Balance — shot range, speed, orb count, core sizes — decides the fight for everyone in the arena, so it comes from the server and cannot be changed here. Online, the arena assigns your core colour too.",
  settingsApply: "APPLY SETTINGS",
  settingsReset: "BACK TO DEFAULTS",
  settingsHint: "Visual settings apply at once; match length applies to the next match.",
  settingsEyebrow: "PROTOTYPE CONTROLS",
  settingsAppearance: "APPEARANCE",
  aimRange: "RANGE",
  shieldAria: "Energy shield",
  launchAria: "Launch an orbit orb",
  boostAria: "Nova boost",
  firstToast: "COLLECT ENERGY · GROW YOUR ORBIT",
  languageAria: "Language",

  standingsTitle: "STANDINGS",
  standingsSub: "N Nova: Orbit Clash · world board",
  standingsBack: "BACK TO THE ARENA",
  periodDaily: "TODAY",
  periodDailyBlurb: "Resets at midnight. Guests are here too.",
  periodWeekly: "THIS WEEK",
  periodWeeklyBlurb: "Starts on Monday. Signed-in players only.",
  periodAllTime: "ALL TIME",
  periodAllTimeBlurb: "Records that stay. Signed-in players only.",
  standingsLoading: "Loading…",
  standingsFailed: "The standings could not be loaded. Trying again shortly.",
  standingsEmptyDaily: "Nobody has scored today. Be the first.",
  standingsEmptyRanked: "This board is for signed-in players. Sign in with Google to make your record last.",
  standingsNote:
    "Scores are written by the arena itself: a life enters the board with what it collected between spawning and ending. There is nothing here to submit a score to.",
  verifiedAccount: "Google account",
  standingsDocTitle: "Standings · N Nova: Orbit Clash",
};

const DICTIONARIES: Record<Language, Dictionary> = { tr, en };

export type MessageKey = keyof Dictionary;

/**
 * Stored choice first, then what the browser asks for. Anything that is not
 * Turkish gets English rather than a language nobody on the page speaks.
 */
export function resolveLanguage(): Language {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === "tr" || stored === "en") return stored;
  } catch { /* private mode */ }
  const preferred = typeof navigator === "undefined" ? "" : (navigator.language ?? "");
  return preferred.toLowerCase().startsWith("tr") ? "tr" : "en";
}

export function rememberLanguage(language: Language) {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch { /* private mode */ }
  document.documentElement.lang = language;
}

/** `t("joinedRoom", { room })` — placeholders are `{name}` and never positional. */
export function translator(language: Language) {
  const dictionary = DICTIONARIES[language] ?? DICTIONARIES.tr;
  return (key: MessageKey, values?: Record<string, string | number>) => {
    const template = dictionary[key];
    if (!values) return template;
    return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
      Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : whole);
  };
}
